import React, { useEffect, useState } from "react"

import axios from "axios"
import { detect } from "detect-browser"
import { Link, graphql, useStaticQuery } from "gatsby"
import PropTypes from "prop-types"
import { useGoogleReCaptcha } from "react-google-recaptcha-v3"

import * as styles from "./bracket-summary.module.scss"
import { getUser, handleCsrf, isLoggedIn, logout } from "../../../helpers/auth"
import {
  getBrackets,
  getTournament,
  isBracketClosed,
  stringWithDates,
} from "../../../helpers/bracket-api"
import DarkColor from "../../../helpers/dark-color"
import UrlFunctions from "../../../helpers/url"
import Button from "../../atoms/button"
import CopyContainer from "../../atoms/copy-container"
import Grid from "../../atoms/grid"
import Heading from "../../atoms/heading"
import Logo from "../../atoms/logo"
import Title from "../../atoms/title"
import Form from "../../molecules/form"
import FormState from "../../molecules/form-state"
import Login from "../login"

const BracketSummary = ({ bracket, path }) => {
  const {
    site,
    sanitySettings,
    sanityVertical,
    allSanityCountry,
    allSanityDictionary,
  } = useStaticQuery(graphql`
    {
      site {
        siteMetadata {
          siteUrl
        }
      }
      sanityVertical(slug: { current: { eq: "daily-sports" } }) {
        cm_id
      }
      allSanityCountry {
        nodes {
          abbreviation
          url
        }
      }
      sanitySettings {
        bracket_early
      }
      allSanityDictionary {
        nodes {
          key {
            current
          }
          value
        }
      }
    }
  `)
  const [formState, setFormState] = useState("")
  const [tournament, setTournament] = useState()
  const [brackets, setBrackets] = useState([])
  const [user, setUser] = useState("")
  const [email, setEmail] = useState("")
  const [mode, setMode] = useState("loggedOut")
  const color = (bracket.color || bracket.sport.color)?.replaceAll("-", "")
  const presentingPartner = bracket.presenting_partners?.find(
    (b) => !b.country || b.country.abbreviation === process.env.GATSBY_COUNTRY
  )
  const secondaryPartners = bracket.secondary_partners.filter(
    (b) => !b.country || b.country.abbreviation === process.env.GATSBY_COUNTRY
  )
  const partners = presentingPartner
    ? [presentingPartner, ...secondaryPartners]
    : secondaryPartners
  const country = allSanityCountry.nodes.find(
    (c) => c.url === site.siteMetadata.siteUrl
  ).abbreviation
  const [partner, setPartner] = useState("")

  useEffect(() => {
    handleCsrf()
    setMode(isLoggedIn() ? "loggedIn" : "loggedOut")
    getTournament(bracket.id).then((data) => setTournament(data))
    getBrackets(bracket.id).then((data) => setBrackets(data))
    getUser((data) => {
      setUser(data?.first_name)
      setEmail(data?.email)
    })
    setPartner(UrlFunctions.getParams().partner)
  }, [])

  const { executeRecaptcha } = useGoogleReCaptcha()

  const handleSubmit = async (e) => {
    setFormState("pending")
    e.preventDefault()
    const token = await executeRecaptcha("submit")
    const customFields = [
      { Key: "ContestID", Value: bracket.slug.current },
      { Key: "ContestName", Value: bracket.title },
      { Key: "Country", Value: country },
    ]
    axios
      .post("/.netlify/functions/createsend", {
        ListId: tournament.mailing_list_id,
        token,
        EmailAddress: email,
        CustomFields: customFields,
        Resubscribe: true,
        Type: "bracket",
        ConsentToTrack: "Yes",
        browser: detect()?.name,
      })
      .then(() => {
        setFormState("success")
      })
      .catch(() => {
        setFormState("error")
      })
  }

  return (
    <Title color={color}>
      <Grid col={2}>
        <div>
          <div className={styles.left}>
            {mode === "loggedIn" && user ? (
              <Heading level={5}>
                {user}
                {
                  allSanityDictionary.nodes.find(
                    (dictionaryItem) =>
                      dictionaryItem.key.current === "bracket_welcome"
                  )?.value
                }
              </Heading>
            ) : (
              ""
            )}
            {partners.length > 0 ? (
              <div className={styles.logo}>
                {partners.map((p) =>
                  (p.partner.dark_logo || p.partner.logo) &&
                  p.type !== "promotion" ? (
                    <Logo
                      logo={
                        DarkColor(color)
                          ? p.partner.dark_logo || p.partner.logo
                          : p.partner.logo
                      }
                      alt={p.name}
                      url={p.url || p.partner.url}
                    />
                  ) : null
                )}
              </div>
            ) : (
              ""
            )}
            <Heading level={2}>
              {bracket.title} {bracket.sport.emoji}
            </Heading>
            <Heading level={6}>{bracket.description}</Heading>
            {mode !== "loggedIn" ? (
              <div>
                {
                  allSanityDictionary.nodes.find(
                    (dictionaryItem) =>
                      dictionaryItem.key.current === "bracket_login"
                  )?.value
                }
              </div>
            ) : (
              ""
            )}
          </div>
        </div>
        <div>
          {mode === "loggedIn" ? (
            <>
              {brackets.length > 0 ? (
                <CopyContainer bg={color} className={styles.container}>
                  <Heading level={5}>
                    {
                      allSanityDictionary.nodes.find(
                        (dictionaryItem) =>
                          dictionaryItem.key.current === "brackets_yours"
                      )?.value
                    }
                  </Heading>
                  <Heading level={6} semantic={false}>
                    <ul>
                      {brackets.map((userBracket) => (
                        <li>
                          <Link
                            to={`/bracket-challenge/${bracket.slug.current}/${userBracket.id}`}
                          >
                            {userBracket.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </Heading>
                </CopyContainer>
              ) : (
                ""
              )}
              {isBracketClosed(tournament) === "early" ? (
                <>
                  {!path?.includes("/new/") ? (
                    <CopyContainer>
                      {stringWithDates(bracket, sanitySettings.bracket_early)}
                    </CopyContainer>
                  ) : (
                    ""
                  )}
                  <FormState condition={formState !== "success"}>
                    <Form
                      handleSubmit={handleSubmit}
                      errorMessage={
                        allSanityDictionary.nodes.find(
                          (item) => item.key.current === "error_message"
                        )?.value
                      }
                      submitText={
                        allSanityDictionary.nodes.find(
                          (item) => item.key.current === "bracket_reminder"
                        )?.value
                      }
                      formState={formState}
                    />
                  </FormState>
                </>
              ) : (
                <div className={styles.buttons}>
                  {tournament &&
                  (!tournament.max_brackets_per_user ||
                    tournament.max_brackets_per_user > brackets.length) &&
                  !path?.includes("/new/") &&
                  !isBracketClosed(tournament) ? (
                    <Button
                      url={`/bracket-challenge/${bracket.slug.current}/new`}
                    >
                      {
                        allSanityDictionary.nodes.find(
                          (dictionaryItem) =>
                            dictionaryItem.key.current === "bracket_submit"
                        )?.value
                      }
                    </Button>
                  ) : (
                    ""
                  )}
                  <Button
                    variant="secondary"
                    pressEvent={() => logout(() => window.location.reload())}
                  >
                    {
                      allSanityDictionary.nodes.find(
                        (dictionaryItem) =>
                          dictionaryItem.key.current === "logout"
                      )?.value
                    }
                  </Button>
                </div>
              )}
            </>
          ) : (
            <Login
              light={DarkColor(color)}
              standalone={false}
              cmId={
                isBracketClosed(tournament) === "late"
                  ? sanityVertical.cm_id
                  : tournament?.mailing_list_id
              }
              customFields={JSON.stringify([
                { ContestID: bracket.slug.current },
                { ContestName: bracket.title },
                { Partner: partner },
                { Country: country },
              ])}
            />
          )}
        </div>
      </Grid>
    </Title>
  )
}

export default BracketSummary

BracketSummary.propTypes = {
  bracket: PropTypes.object.isRequired,
  path: PropTypes.string.isRequired,
}
